import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import config from "./config/config";
import { useAuth } from "./hooks/useAuth";
import { useDispatch } from "react-redux";
import { addBookmark, setNotifications } from "./store/appSlices";

const SocketContext = createContext();

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
    const { authData } = useAuth();
    const dispatch = useDispatch();
    const token = authData?.accessToken;
    const refreshToken = authData?.refreshToken;

    const [socket, setSocket] = useState(null);
    const [isOnline, setIsOnline] = useState(false);
    const reconnectAttempts = useRef(0);

    useEffect(() => {
        let socketInstance;

        const connectWebSocket = () => {
            if (!token) return;

            socketInstance = new WebSocket(config.SOCKET_CONNECTION_URL);

            // Handle connection open
            socketInstance.onopen = () => {
                if (window.location.hostname === "localhost") {
                    console.log("Connected to WebSocket server");
                }
                socketInstance.send(
                    JSON.stringify({
                        type: "authenticate",
                        token: token
                    })
                );
                setIsOnline(true);
                reconnectAttempts.current = 0; // Reset reconnect attempts on successful connection
            };

            // Handle disconnection and reconnect
            socketInstance.onclose = () => {
                if (window.location.hostname === "localhost") {
                    console.log("Disconnected from WebSocket server");
                }
                setIsOnline(false);

                if (reconnectAttempts.current < 5) {
                    const retryDelay = Math.min(1000 * 2 ** reconnectAttempts.current, 30000); // Exponential backoff
                    setTimeout(() => {
                        reconnectAttempts.current += 1;
                        connectWebSocket();
                    }, retryDelay);
                }
            };

            // Handle errors
            socketInstance.onerror = (error) => {
                console.error("WebSocket error:", error);
                socketInstance.close(); // Ensure disconnection on error
            };

            // Handle server messages
            socketInstance.onmessage = (event) => {
                const data = JSON.parse(event.data);

                switch (data.event) {
                    case "bookmarks-update":
                        dispatch(addBookmark(data.payload));
                        break;
                    case "personalized-content-update":
                        dispatch(setNotifications(data.payload));
                    // case "notifications":
                    //     dispatch(setNotifications(data.payload));
                        break;
                    case "token-expired":
                        refreshToken().then((newToken) => {
                            // Reauthenticate after token refresh
                            socketInstance.send(
                                JSON.stringify({
                                    type: "authenticate",
                                    token: newToken
                                })
                            );
                        });
                        break;
                    default:
                        console.warn("Unhandled event:", data);
                }
            };

            setSocket(socketInstance);
        };

        connectWebSocket();

        // Cleanup on component unmount
        return () => {
            socketInstance?.close();
        };
    }, [token, dispatch, refreshToken]);

    return (
        <SocketContext.Provider value={{ socket, isOnline }}>
            {children}
        </SocketContext.Provider>
    );
};
